/**
 * Illustrated bilingual chapters for the magazine flipbook.
 * Each chapter is rendered as a comic-panel layout. Content is simple
 * (like for a 5-year-old) and matches the official Canales manuals.
 */

export const CHAPTERS = [
  {
    id: "cover",
    kind: "cover",
    title: { es: "Canales Furniture — La Revista", en: "Canales Furniture — The Magazine" },
    subtitle: {
      es: "Cómo vender con el corazón (y cerrar más ventas)",
      en: "How to sell from the heart (and close more sales)",
    },
  },
  {
    id: "intro",
    title: { es: "Bienvenidos", en: "Welcome" },
    scenes: [
      {
        speaker: "carlos",
        text: {
          es: "¡Hola! Soy Carlos, tu mentor. Vamos a aprender a vender muebles como campeones.",
          en: "Hi! I'm Carlos, your mentor. We're going to learn to sell furniture like champs.",
        },
      },
      {
        speaker: "ana",
        text: {
          es: "Yo soy Ana. Empecé nueva y en 3 meses fui la mejor vendedora. ¡Tú también puedes!",
          en: "I'm Ana. I started brand new and in 3 months I was the top seller. You can do it too!",
        },
      },
    ],
    fact: {
      variant: "did_you_know",
      title_es: "¿SABÍAS QUÉ?",
      title_en: "DID YOU KNOW?",
      text: {
        es: "Canales Furniture tiene más de 20 años haciendo casas más felices en Texas y Oklahoma.",
        en: "Canales Furniture has been making homes happier in Texas and Oklahoma for over 20 years.",
      },
    },
  },
  {
    id: "prep",
    title: { es: "Preparación", en: "Preparation" },
    scenes: [
      {
        speaker: "ana",
        text: {
          es: "Antes de abrir la tienda, revisamos precios, promociones y el piso de venta. ¡Preparación es poder!",
          en: "Before opening, we check prices, promos and the sales floor. Preparation is power!",
        },
      },
    ],
    fact: {
      variant: "remember",
      title_es: "RECUERDA",
      title_en: "REMEMBER",
      text: {
        es: "Un vendedor preparado transmite confianza. El cliente lo siente en 5 segundos.",
        en: "A prepared seller radiates trust. The customer feels it in 5 seconds.",
      },
    },
    dos: {
      do: {
        es: ["Llegar 15-30 min antes", "Repasar promociones", "Verificar limpieza del piso"],
        en: ["Arrive 15-30 min early", "Review promotions", "Check floor cleanliness"],
      },
      dont: {
        es: ["Entrar corriendo al último minuto", "Ignorar las ofertas del día", "Empezar sin plan"],
        en: ["Rush in at the last minute", "Ignore today's deals", "Start without a plan"],
      },
    },
  },
  {
    id: "greet",
    title: { es: "GREET — El Saludo", en: "GREET — The Greeting" },
    scenes: [
      {
        speaker: "carlos",
        text: {
          es: "Sonríe, mira a los ojos y di tu nombre. Ese saludo vale más que 10 descuentos.",
          en: "Smile, look them in the eyes, say your name. That greeting is worth more than 10 discounts.",
        },
      },
    ],
    fact: {
      variant: "important",
      title_es: "IMPORTANTE",
      title_en: "IMPORTANT",
      text: {
        es: "Nunca preguntes '¿te puedo ayudar?'. Los clientes dicen 'no gracias' por reflejo.",
        en: "Never ask 'can I help you?' — customers reflex-answer 'no thanks'.",
      },
    },
    dos: {
      do: {
        es: ["Sonreír de verdad", "Presentarte por tu nombre", "Hacer sentir al cliente como en casa"],
        en: ["Smile for real", "Say your name", "Make them feel at home"],
      },
      dont: {
        es: ["Perseguir al cliente", "Sonar como robot", "Preguntar '¿te puedo ayudar?'"],
        en: ["Chase the customer", "Sound like a robot", "Ask 'can I help you?'"],
      },
    },
  },
  {
    id: "discover",
    title: { es: "DISCOVER — Descubre", en: "DISCOVER — Discover" },
    scenes: [
      {
        speaker: "ana",
        text: {
          es: "Haz preguntas abiertas: '¿Para qué habitación?', '¿Cuántas personas viven?', '¿Le gusta ver TV en familia?'",
          en: "Ask open questions: 'Which room?', 'How many people live there?', 'Do you enjoy family TV time?'",
        },
      },
    ],
    fact: {
      variant: "did_you_know",
      title_es: "¿SABÍAS QUÉ?",
      title_en: "DID YOU KNOW?",
      text: {
        es: "El 80% de la venta ocurre cuando escuchas más de lo que hablas.",
        en: "80% of the sale happens when you listen more than you talk.",
      },
    },
    dos: {
      do: {
        es: ["Preguntas abiertas", "Tomar nota mental", "Repetir lo que dice el cliente para confirmar"],
        en: ["Open questions", "Take mental notes", "Repeat back to confirm"],
      },
      dont: {
        es: ["Solo preguntas de sí/no", "Interrumpir", "Suponer lo que quiere"],
        en: ["Yes/no questions only", "Interrupt", "Assume what they want"],
      },
    },
  },
  {
    id: "demonstrate",
    title: { es: "DEMONSTRATE — Demuestra", en: "DEMONSTRATE — Demonstrate" },
    scenes: [
      {
        speaker: "carlos",
        text: {
          es: "Invita al cliente a sentarse, a tocar la tela, a probar el mueble. ¡Los sentidos venden!",
          en: "Invite the customer to sit, touch the fabric, try the piece. Senses sell!",
        },
      },
    ],
    fact: {
      variant: "remember",
      title_es: "RECUERDA F.A.B.",
      title_en: "REMEMBER F.A.B.",
      text: {
        es: "Feature (característica) → Advantage (ventaja) → Benefit (beneficio). El beneficio es lo que vende.",
        en: "Feature → Advantage → Benefit. The benefit is what sells.",
      },
    },
    dos: {
      do: {
        es: ["Que el cliente pruebe el mueble", "Contar historias de otros clientes", "Explicar el BENEFICIO para su vida"],
        en: ["Let them try the piece", "Share happy customer stories", "Explain the BENEFIT for their life"],
      },
      dont: {
        es: ["Solo hablar sin dejar tocar", "Vender por características técnicas", "Presionar"],
        en: ["Just talk without touching", "Sell on tech specs only", "Push hard"],
      },
    },
  },
  {
    id: "close",
    title: { es: "CLOSE — Cierra", en: "CLOSE — Close" },
    scenes: [
      {
        speaker: "ana",
        text: {
          es: "Usa preguntas de cierre alternativo: '¿Se lo entregamos jueves o sábado?' Asume la venta.",
          en: "Use alternative closes: 'Do we deliver Thursday or Saturday?' Assume the sale.",
        },
      },
    ],
    fact: {
      variant: "common_error",
      title_es: "ERROR COMÚN",
      title_en: "COMMON MISTAKE",
      text: {
        es: "Preguntar '¿lo quiere sí o no?' pone al cliente en modo defensa. Da opciones, no ultimátums.",
        en: "Asking 'do you want it yes or no?' makes customers defensive. Give options, not ultimatums.",
      },
    },
    dos: {
      do: {
        es: ["Cierre alternativo", "Ofrecer financiamiento como puente", "Mantener la calma"],
        en: ["Alternative close", "Offer financing as a bridge", "Stay calm"],
      },
      dont: {
        es: ["Presionar con urgencia falsa", "Bajar precio a la primera", "Mostrar nervios"],
        en: ["Push with fake urgency", "Cut price on first ask", "Show nerves"],
      },
    },
  },
  {
    id: "followup",
    title: { es: "FOLLOW-UP — Seguimiento", en: "FOLLOW-UP — Follow-up" },
    scenes: [
      {
        speaker: "carlos",
        text: {
          es: "Llama 24-48h después de la entrega. Un cliente feliz trae 3 amigos más.",
          en: "Call 24-48h after delivery. A happy customer brings 3 more friends.",
        },
      },
    ],
    fact: {
      variant: "did_you_know",
      title_es: "¿SABÍAS QUÉ?",
      title_en: "DID YOU KNOW?",
      text: {
        es: "El follow-up puede multiplicar tus ventas por 3 gracias a los referidos.",
        en: "Follow-ups can triple your sales through referrals.",
      },
    },
  },
  {
    id: "complaints",
    title: { es: "Manejo de Quejas", en: "Managing Complaints" },
    scenes: [
      {
        speaker: "ana",
        text: {
          es: "Una queja es un regalo: el cliente te dice cómo mejorar. Escucha, disculpa, resuelve.",
          en: "A complaint is a gift: the customer tells you how to improve. Listen, apologize, solve.",
        },
      },
    ],
    fact: {
      variant: "important",
      title_es: "IMPORTANTE",
      title_en: "IMPORTANT",
      text: {
        es: "El 70% de los clientes que se quejan Y son escuchados se vuelven fieles para siempre.",
        en: "70% of complaining customers who feel heard stay loyal forever.",
      },
    },
    dos: {
      do: {
        es: ["Escuchar con empatía", "Disculparte sinceramente", "Ofrecer solución concreta"],
        en: ["Listen with empathy", "Apologize sincerely", "Offer a concrete solution"],
      },
      dont: {
        es: ["Defender a la tienda", "Culpar a otros", "Ignorar el problema"],
        en: ["Defend the store", "Blame others", "Ignore the problem"],
      },
    },
  },
  {
    id: "communication",
    title: { es: "Comunicación", en: "Communication" },
    scenes: [
      {
        speaker: "carlos",
        text: {
          es: "Usa lenguaje simple. Si el cliente no entiende, la culpa siempre es del vendedor.",
          en: "Use simple language. If the customer doesn't understand, it's always the seller's fault.",
        },
      },
    ],
    fact: {
      variant: "remember",
      title_es: "RECUERDA",
      title_en: "REMEMBER",
      text: {
        es: "Habla con palabras que un niño de 5 años entienda. Es respeto, no simpleza.",
        en: "Speak with words a 5-year-old understands. That's respect, not simplicity.",
      },
    },
  },
];
